import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Map, Plus } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Areas section embedded inside Branch Management.
 *
 * tenant_admin / platform_admin only — uses POST/GET /api/areas.
 * Each area is a named grouping of branch_ids; area_managers are scoped to
 * their assigned areas via the user invite flow.
 */
export default function AreasSection({ branches }) {
  const { canManage } = useAuth();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', branch_ids: [] });

  useEffect(() => { fetchAreas(); }, []);

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/areas`, { credentials: 'include' });
      if (res.ok) setAreas(await res.json());
    } catch (e) { console.error('Error loading areas:', e); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) { toast.error('Area name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/areas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: form.name.trim(), branch_ids: form.branch_ids }),
      });
      if (res.ok) {
        toast.success('Area created');
        setOpen(false);
        setForm({ name: '', branch_ids: [] });
        fetchAreas();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create area');
      }
    } catch { toast.error('Failed to create area'); }
    finally { setSaving(false); }
  };

  const branchName = (id) => branches.find(b => b.branch_id === id)?.name || id;

  if (!canManage) return null;

  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="areas-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
          <Map className="w-5 h-5 text-[#1A3E6F]" /> Areas
        </CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#1A3E6F] hover:bg-[#122c50] text-white" data-testid="create-area-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Area
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[480px]" data-testid="create-area-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat']">Create Area</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="area-name">Area Name *</Label>
                <Input
                  id="area-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. North Region"
                  data-testid="area-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Assigned Branches</Label>
                <div className="grid grid-cols-1 gap-1 max-h-44 overflow-y-auto p-2 border rounded-lg" data-testid="area-branches-multiselect">
                  {branches.length === 0 ? (
                    <p className="text-sm text-[#64748B]">No branches yet — create branches first.</p>
                  ) : branches.map((b) => (
                    <label key={b.branch_id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.branch_ids.includes(b.branch_id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...form.branch_ids, b.branch_id]
                            : form.branch_ids.filter(id => id !== b.branch_id);
                          setForm({ ...form, branch_ids: ids });
                        }}
                        className="rounded"
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#1A3E6F] hover:bg-[#122c50]" data-testid="save-area-btn">
                  {saving ? 'Creating…' : 'Create Area'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-[#64748B]">Loading…</p>
        ) : areas.length === 0 ? (
          <p className="text-sm text-[#64748B]">
            No areas yet. Create an area to group branches and scope Area Manager access.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="areas-list">
            {areas.map((a) => (
              <li key={a.area_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-[#F1F5F9] rounded-lg" data-testid={`area-row-${a.area_id}`}>
                <div className="font-medium text-[#0F172A]">{a.name}</div>
                <div className="text-xs text-[#64748B] flex flex-wrap gap-1">
                  {(a.branch_ids || []).length === 0 ? (
                    <span className="italic">No branches assigned</span>
                  ) : (a.branch_ids || []).map((bid) => (
                    <span key={bid} className="px-2 py-0.5 rounded-full bg-[#F1F5F9] border border-[#E2E8F0]">
                      {branchName(bid)}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
